import * as types from '../types';
import { INotificationsApi } from '../contracts';
import { HttpApi } from './HttpApi';

const BASE = '/notifications';
const enc = encodeURIComponent;

export class NotificationsHttpApi extends HttpApi implements INotificationsApi {
  async listNotificationTemplates(): Promise<types.NotificationTemplate[]> {
    const response = await this.http.get<types.APIResponse<types.NotificationTemplate[]>>(`${BASE}/templates`);
    return response.data.data;
  }

  async updateNotificationTemplate(eventKey: string, data: types.UpdateNotificationTemplateRequest): Promise<types.NotificationTemplate> {
    const response = await this.http.put<types.APIResponse<types.NotificationTemplate>>(`${BASE}/templates/${enc(eventKey)}`, data);
    return response.data.data;
  }

  async previewNotificationTemplate(eventKey: string, data: { subject?: string; body?: string }): Promise<types.NotificationTemplatePreview> {
    const response = await this.http.post<types.APIResponse<types.NotificationTemplatePreview>>(
      `${BASE}/templates/${enc(eventKey)}/preview`,
      data
    );
    return response.data.data;
  }

  async listOutbox(filters: types.OutboxListFilters = {}, page = 1, limit = 50): Promise<types.PaginatedResponse<types.OutboxMessage>> {
    // Blank filters are left out: the server rejects e.g. status='' as invalid
    const params: Record<string, string | number> = { page, limit };
    (['customerId', 'status', 'eventKey', 'sourceType', 'sourceId', 'startDate', 'endDate'] as const).forEach((key) => {
      const value = filters[key];
      if (value) params[key] = value;
    });
    const response = await this.http.get<types.PaginatedResponse<types.OutboxMessage>>(`${BASE}/outbox`, { params });
    return response.data;
  }

  async retryOutboxMessage(id: string): Promise<types.OutboxMessage> {
    const response = await this.http.post<types.APIResponse<types.OutboxMessage>>(`${BASE}/outbox/${enc(id)}/retry`, {});
    return response.data.data;
  }

  async cancelOutboxMessage(id: string): Promise<types.OutboxMessage> {
    const response = await this.http.post<types.APIResponse<types.OutboxMessage>>(`${BASE}/outbox/${enc(id)}/cancel`, {});
    return response.data.data;
  }

  async emailInvoice(invoiceId: string, idempotencyKey?: string): Promise<types.NotificationSendResult> {
    const response = await this.http.post<types.APIResponse<types.NotificationSendResult>>(
      `${BASE}/send/invoice/${enc(invoiceId)}`,
      idempotencyKey ? { idempotencyKey } : {}
    );
    return response.data.data;
  }

  async emailStatement(customerId: string, idempotencyKey?: string): Promise<types.NotificationSendResult> {
    const response = await this.http.post<types.APIResponse<types.NotificationSendResult>>(
      `${BASE}/send/statement/${enc(customerId)}`,
      idempotencyKey ? { idempotencyKey } : {}
    );
    return response.data.data;
  }

  async sendTestNotification(to?: string): Promise<types.NotificationSendResult> {
    const response = await this.http.post<types.APIResponse<types.NotificationSendResult>>(`${BASE}/test`, to ? { to } : {});
    return response.data.data;
  }

  async sendTestNotificationTemplate(
    eventKey: string,
    data: { subject?: string; body?: string; to?: string }
  ): Promise<types.NotificationSendResult> {
    const { to, ...text } = data;
    const response = await this.http.post<types.APIResponse<types.NotificationSendResult>>(
      `${BASE}/templates/${enc(eventKey)}/test`,
      to && to.trim() ? { ...text, to: to.trim() } : text
    );
    return response.data.data;
  }

  async listNotificationProviders(): Promise<types.NotificationProviderDescriptor[]> {
    const response = await this.http.get<types.APIResponse<types.NotificationProviderDescriptor[]>>(`${BASE}/providers`, {
      params: { channel: 'EMAIL' },
    });
    return response.data.data;
  }

  async getNotificationProvider(): Promise<types.NotificationProviderConfig | null> {
    const response = await this.http.get<types.APIResponse<types.NotificationProviderConfig | null>>(`${BASE}/provider`, {
      params: { channel: 'EMAIL' },
    });
    return response.data.data ?? null;
  }

  async saveNotificationProvider(data: types.SaveNotificationProviderRequest): Promise<types.NotificationProviderConfig> {
    const response = await this.http.put<types.APIResponse<types.NotificationProviderConfig>>(`${BASE}/provider`, data);
    return response.data.data;
  }

  async verifyNotificationProvider(): Promise<types.VerifyNotificationProviderResult> {
    const response = await this.http.post<types.APIResponse<types.VerifyNotificationProviderResult>>(`${BASE}/provider/verify`, {
      channel: 'EMAIL',
    });
    return response.data.data;
  }

  async getNotificationSettings(): Promise<types.NotificationSettings> {
    const response = await this.http.get<types.APIResponse<types.NotificationSettings>>(`${BASE}/settings`);
    return response.data.data;
  }

  async updateNotificationSettings(data: types.UpdateNotificationSettingsRequest): Promise<types.NotificationSettings> {
    const response = await this.http.put<types.APIResponse<types.NotificationSettings>>(`${BASE}/settings`, data);
    return response.data.data;
  }

  async getJobCustomerNotifications(jobId: string): Promise<types.JobCustomerNotifications> {
    const response = await this.http.get<types.APIResponse<types.JobCustomerNotifications>>(
      `/jobs/${enc(jobId)}/customer-notifications`
    );
    return response.data.data;
  }

  async sendJobCustomerNotification(
    jobId: string,
    eventKey: types.DayOfEventKey,
    idempotencyKey?: string
  ): Promise<types.SendJobCustomerNotificationResult> {
    const response = await this.http.post<types.APIResponse<types.SendJobCustomerNotificationResult>>(
      `/jobs/${enc(jobId)}/customer-notifications`,
      idempotencyKey ? { eventKey, idempotencyKey } : { eventKey }
    );
    return response.data.data;
  }

  async getPublicInvoiceView(token: string): Promise<types.PublicInvoiceView> {
    const response = await this.http.get<types.APIResponse<types.PublicInvoiceView>>(`/public/invoices/${enc(token)}`);
    return response.data.data;
  }

  async getPublicStatementView(token: string): Promise<types.PublicStatementView> {
    const response = await this.http.get<types.APIResponse<types.PublicStatementView>>(`/public/statements/${enc(token)}`);
    return response.data.data;
  }
}
